import React, { useState, lazy, Suspense, useEffect, useRef, useMemo } from 'react';
import { Iztrolabe } from '../Iztrolabe';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl';
import { useAIChat } from '../AIChat/AIChatContext';
import { HistoryManager } from '../utils/historyUtils';
// Lazy loading components
const AIChat = lazy(() => import("../AIChat").then(module => ({ default: module.AIChat })));
const BackgroundAIIndicator = lazy(() => import("../AIChat/BackgroundAIIndicator"));
const HistoryPanel = lazy(() => import("../AIChat/components/HistoryPanel"));
/**
 * Internal component that contains the main UI and logic,
 * and has access to the AIChatContext.
 */
const ResultContainerWithHistory = ({ chartMode, formData, synastryData, astrolabe, astrolabe1, astrolabe2, onShowInputPanel, }) => {
    const [activeTab, setActiveTab] = useState("ai");
    const { createSession, createSessionForHistory, switchToSessionForHistory, getSessionForHistory, sessions, activeSessionId } = useAIChat();
    const isInitialMount = useRef(true);
    // 根据当前排盘数据生成历史记录查找键
    const currentHistoryKey = useMemo(() => {
        if (chartMode === "single") {
            return `${formData.name}-${formData.year}-${formData.month}-${formData.day}-${formData.birthTime}-${formData.gender}`;
        }
        else {
            return `${synastryData.person1.name}-${synastryData.person1.year}-${synastryData.person1.month}-${synastryData.person1.day}-${synastryData.person1.birthTime}-${synastryData.person1.gender}_${synastryData.person2.name}-${synastryData.person2.year}-${synastryData.person2.month}-${synastryData.person2.day}-${synastryData.person2.birthTime}-${synastryData.person2.gender}`;
        }
    }, [chartMode, formData, synastryData]);
    // 查找当前排盘对应的历史记录ID
    const currentHistoryId = useMemo(() => {
        const history = HistoryManager.getHistory();
        const matchingItem = history.find(item => {
            if (chartMode === "single") {
                return item.type === "single" &&
                    item.name === formData.name &&
                    item.year === formData.year &&
                    item.month === formData.month &&
                    item.day === formData.day &&
                    item.birthTime === formData.birthTime &&
                    item.gender === formData.gender;
            }
            else {
                return item.type === "synastry" &&
                    item.name === synastryData.person1.name &&
                    item.year === synastryData.person1.year &&
                    item.month === synastryData.person1.month &&
                    item.day === synastryData.person1.day &&
                    item.birthTime === synastryData.person1.birthTime &&
                    item.gender === synastryData.person1.gender &&
                    item.person2?.name === synastryData.person2.name &&
                    item.person2?.year === synastryData.person2.year &&
                    item.person2?.month === synastryData.person2.month &&
                    item.person2?.day === synastryData.person2.day &&
                    item.person2?.birthTime === synastryData.person2.birthTime &&
                    item.person2?.gender === synastryData.person2.gender;
            }
        });
        return matchingItem?.id;
    }, [chartMode, formData, synastryData]);
    // 生成排盘数据用于会话关联
    const chartDataForSession = useMemo(() => {
        if (chartMode === "single") {
            return {
                type: 'single',
                person1Name: formData.name || "未知",
                birthDate: `${formData.year}-${formData.month}-${formData.day}`
            };
        }
        else {
            return {
                type: 'synastry',
                person1Name: synastryData.person1.name || "第一个人",
                person2Name: synastryData.person2.name || "第二个人",
                birthDate: `${synastryData.person1.year}-${synastryData.person1.month}-${synastryData.person1.day}`
            };
        }
    }, [chartMode, formData, synastryData]);
    // 处理会话切换逻辑
    useEffect(() => {
        // Skip the very first render
        if (isInitialMount.current) {
            isInitialMount.current = false;
            // If no sessions exist on first load (from localStorage), create one.
            if (sessions.length === 0) {
                if (currentHistoryId) {
                    createSessionForHistory(currentHistoryId, chartDataForSession);
                }
                else {
                    createSession();
                }
            }
            return;
        }
        // 检查是否有对应的聊天会话
        if (currentHistoryId) {
            const existingSession = getSessionForHistory(currentHistoryId);
            if (existingSession) {
                // 切换到已存在的会话
                switchToSessionForHistory(currentHistoryId);
                console.log(`切换到历史记录 ${currentHistoryId} 的聊天会话`);
            }
            else {
                // 创建新的关联会话
                createSessionForHistory(currentHistoryId, chartDataForSession);
                console.log(`为历史记录 ${currentHistoryId} 创建新的聊天会话`);
            }
        }
        else {
            // 没有对应的历史记录，创建普通会话
            createSession();
            console.log('创建新的普通聊天会话');
        }
        setActiveTab('ai'); // Switch to AI tab to show the new chat
    }, [currentHistoryId, currentHistoryKey, chartDataForSession]);
    const handleNewSession = () => {
        createSession();
        setActiveTab('ai');
    };
    return (React.createElement("div", { className: "result-section" },
        React.createElement("div", { className: "tab-navigation" },
            React.createElement("div", { className: "tab-navigation-spacer" }),
            React.createElement("div", { className: "tab-navigation-center" },
                React.createElement(SegmentedControl, { name: "activeTab", options: [
                        { label: "星盘", value: "astrolabe" },
                        { label: "AI 解读", value: "ai" },
                        { label: "历史", value: "history" },
                    ], value: activeTab, onChange: (value) => setActiveTab(value) })),
            React.createElement("div", { className: "tab-navigation-actions" },
                React.createElement("button", { onClick: handleNewSession, className: "new-chat-button", "aria-label": "\u65B0\u5EFA\u8BB0\u5F55" },
                    React.createElement("svg", { width: "22", height: "22", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                        React.createElement("path", { d: "M14.5 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V7.5L14.5 2Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
                        React.createElement("path", { d: "M14 2V8H20", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
                        React.createElement("path", { d: "M12 18V12", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }),
                        React.createElement("path", { d: "M9 15H15", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }))),
                React.createElement(Suspense, { fallback: null },
                    React.createElement(BackgroundAIIndicator, null)))),
        React.createElement("div", { className: "tab-content" },
            activeTab === "astrolabe" && (React.createElement("div", { className: "astrolabe-content" },
                React.createElement("div", { className: "astrolabe-container" },
                    chartMode === "single" ? (React.createElement(Iztrolabe, { birthday: `${formData.year}-${String(formData.month).padStart(2, '0')}-${String(formData.day).padStart(2, '0')}`, birthTime: formData.birthTime, birthdayType: formData.birthdayType, gender: formData.gender })) : (React.createElement("div", { className: "synastry-container" },
                        React.createElement("div", { className: "synastry-person" },
                            React.createElement("h3", null, synastryData.person1.name || "第一个人"),
                            React.createElement(Iztrolabe, { birthday: `${synastryData.person1.year}-${String(synastryData.person1.month).padStart(2, '0')}-${String(synastryData.person1.day).padStart(2, '0')}`, birthTime: synastryData.person1.birthTime, birthdayType: synastryData.person1.birthdayType, gender: synastryData.person1.gender })),
                        React.createElement("div", { className: "synastry-person" },
                            React.createElement("h3", null, synastryData.person2.name || "第二个人"),
                            React.createElement(Iztrolabe, { birthday: `${synastryData.person2.year}-${String(synastryData.person2.month).padStart(2, '0')}-${String(synastryData.person2.day).padStart(2, '0')}`, birthTime: synastryData.person2.birthTime, birthdayType: synastryData.person2.birthdayType, gender: synastryData.person2.gender })))),
                    React.createElement("div", { className: "rechart-button-container" },
                        React.createElement("button", { onClick: onShowInputPanel, className: "rechart-button" }, chartMode === "single" ? "重新排盘" : "重新合盘"))))),
            React.createElement("div", { className: `ai-content ${activeTab === "ai" ? "visible" : "hidden"}` },
                React.createElement(Suspense, { fallback: React.createElement("div", { className: "loading-indicator" }, "\u52A0\u8F7DAI\u804A\u5929\u529F\u80FD\u4E2D...") },
                    React.createElement(AIChat, { formData: chartMode === "single" ? formData : synastryData.person1, astrolabe: chartMode === "single" ? astrolabe : astrolabe1, synastryAstrolabe: chartMode === "synastry" ? astrolabe2 : undefined, isSynastryMode: chartMode === "synastry", personNames: {
                            person1: synastryData.person1.name || "第一个人",
                            person2: synastryData.person2.name || "第二个人"
                        }, synastryData: chartMode === "synastry" ? synastryData : undefined, isVisible: activeTab === "ai" && !!activeSessionId, relatedHistoryId: currentHistoryId }))),
            activeTab === "history" && (React.createElement(Suspense, { fallback: React.createElement("div", { className: "loading-indicator" }, "\u52A0\u8F7D\u5386\u53F2\u8BB0\u5F55\u4E2D...") },
                React.createElement(HistoryPanel, { onSelectSession: () => setActiveTab('ai'), relatedHistoryId: currentHistoryId }))))));
};
/**
 * Main container component that provides the AI Chat context
 * and renders the UI.
 */
export const ResultContainer = (props) => {
    return (React.createElement(ResultContainerWithHistory, { ...props }));
};
export default ResultContainer;
